import { Card, CardContent } from "@/components/ui/card";
import { Shield, Award, Users, TrendingUp, Star, CheckCircle2, Clock, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface PremiumTrustSignalsProps {
  listing: any;
}

export const PremiumTrustSignals = ({ listing }: PremiumTrustSignalsProps) => {
  // Generate consistent dummy stats based on listing ID
  const generateStat = (base: number, variance: number) => {
    const seed = listing.id ? listing.id.charCodeAt(0) : 1;
    return base + Math.floor((seed % variance));
  };

  const stats = [
    { 
      icon: Users, 
      value: listing.profile_views || generateStat(1200, 500).toLocaleString(), 
      label: "Visitas este mes",
      color: "text-blue-600"
    },
    { 
      icon: Star, 
      value: listing.rating || (4.7 + (generateStat(0, 3) / 10)).toFixed(1), 
      label: "Valoración media",
      color: "text-yellow-600"
    },
    { 
      icon: CheckCircle2, 
      value: listing.review_count || generateStat(85, 100), 
      label: "Clientes satisfechos",
      color: "text-green-600"
    },
    { 
      icon: TrendingUp, 
      value: `${generateStat(92, 8)}%`, 
      label: "Tasa de recomendación",
      color: "text-primary"
    },
  ];

  const certifications = listing.certifications && listing.certifications.length > 0 
    ? listing.certifications 
    : [
        "Instalaciones Certificadas ISO 9001",
        "Profesionales Titulados y Certificados",
        "Garantía de Satisfacción 100%",
        "Seguro de Responsabilidad Civil"
      ];

  return (
    <div className="space-y-6">
      {/* Trust Stats */}
      <Card className="border-2 border-primary/20 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Confianza Verificada</h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-4 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors">
                <stat.icon className={`w-8 h-8 mx-auto mb-2 ${stat.color}`} />
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-xs text-muted-foreground font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Certifications & Guarantees */}
      <Card className="border-2 border-green-500/20 bg-gradient-to-br from-green-50/50 to-emerald-50/30 dark:from-green-950/20 dark:to-emerald-950/10">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h3 className="text-xl font-bold">Garantías y Certificaciones</h3>
          </div>
          
          <div className="space-y-3">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-background/60 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <span className="font-medium">{cert}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Results Promise */}
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-6 h-6 text-primary" />
            <h3 className="text-xl font-bold">Nuestra Promesa</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex gap-3">
              <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Primera Clase Gratis</p>
                <p className="text-sm text-muted-foreground">Prueba sin compromiso y decide después</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Resultados Garantizados</p>
                <p className="text-sm text-muted-foreground">O te devolvemos tu dinero - sin preguntas</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Cancela Cuando Quieras</p>
                <p className="text-sm text-muted-foreground">Sin permanencia ni compromisos largos</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
